
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle, AlertCircle, ClockIcon, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/auth/AuthContext';

interface ModuleStatusProps {
  moduleId: string;
}

type StatusInfo = {
  id: string;
  status: string;
  approved_by?: string | null;
  approved_at?: string | null;
  first_name?: string | null;
  last_name?: string | null;
};

const ModuleStatus: React.FC<ModuleStatusProps> = ({ moduleId }) => {
  const { user, isAdmin } = useAuth();
  const queryClient = useQueryClient();
  const [isUpdating, setIsUpdating] = useState(false);
  
  // Fetch module status
  const { data: statusInfo, isLoading } = useQuery({
    queryKey: ['module-status', moduleId],
    queryFn: async () => {
      try {
        // First check if status exists
        const { data, error } = await supabase
          .from('course_build_status')
          .select(`
            id,
            status,
            approved_by,
            approved_at
          `)
          .eq('module_id', moduleId)
          .single();
        
        if (error) {
          if (error.code === 'PGRST116') {
            // No status record exists, create one
            const { data: newStatus, error: createError } = await supabase
              .from('course_build_status')
              .insert({
                module_id: moduleId,
                status: 'draft'
              })
              .select()
              .single();
            
            if (createError) throw createError;
            return newStatus as StatusInfo;
          }
          throw error;
        }
        
        // If we have an approver, get their name
        if (data.approved_by) {
          const { data: userData, error: userError } = await supabase
            .from('user_profiles')
            .select('first_name, last_name')
            .eq('id', data.approved_by)
            .single();
          
          if (!userError && userData) {
            return {
              ...data,
              first_name: userData.first_name,
              last_name: userData.last_name
            } as StatusInfo;
          }
        }
        
        return data as StatusInfo;
      } catch (error) {
        console.error('Error fetching module status:', error);
        toast.error('Failed to load module status');
        return null;
      }
    }
  });

  // Update module status mutation
  const updateStatusMutation = useMutation({
    mutationFn: async (newStatus: string) => {
      setIsUpdating(true);
      
      // For approval, add the current user as approver
      const updateData = newStatus === 'approved' 
        ? { 
            status: newStatus, 
            approved_by: user?.id,
            approved_at: new Date().toISOString()
          }
        : { status: newStatus };
      
      const { data, error } = await supabase
        .from('course_build_status')
        .update(updateData)
        .eq('module_id', moduleId)
        .select()
        .single();
        
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      setIsUpdating(false);
      queryClient.invalidateQueries({ queryKey: ['module-status', moduleId] });
      toast.success('Module status updated successfully');
    },
    onError: (error) => {
      setIsUpdating(false);
      console.error('Error updating module status:', error);
      toast.error('Failed to update module status');
    }
  });

  const handleStatusChange = (status: string) => {
    updateStatusMutation.mutate(status);
  };

  const getStatusBadge = () => {
    if (!statusInfo) return null;
    
    switch (statusInfo.status) {
      case 'draft':
        return <Badge variant="outline" className="bg-gray-100 text-gray-800 border-gray-300">Draft</Badge>;
      case 'in_review':
        return <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300">In Review</Badge>;
      case 'approved':
        return <Badge variant="outline" className="bg-green-100 text-green-800 border-green-300">Approved</Badge>;
      case 'published':
        return <Badge variant="outline" className="bg-blue-100 text-blue-800 border-blue-300">Published</Badge>;
      default:
        return null;
    }
  };

  const getStatusIcon = () => {
    if (!statusInfo) return null;
    
    switch (statusInfo.status) {
      case 'draft':
        return <ClockIcon className="h-5 w-5 text-gray-500" />;
      case 'in_review':
        return <AlertCircle className="h-5 w-5 text-amber-500" />;
      case 'approved':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'published':
        return <Lock className="h-5 w-5 text-blue-500" />;
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <Card className="bg-muted/20">
        <CardContent className="p-4">
          <div className="animate-pulse h-4 w-1/3 bg-muted rounded"></div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-muted/20">
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-medium flex items-center justify-between">
          <span>Module Status</span>
          {getStatusBadge()}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            {getStatusIcon()}
            <span className="text-sm">
              {statusInfo?.status === 'approved' && statusInfo.first_name && statusInfo.last_name 
                ? `Approved by ${statusInfo.first_name} ${statusInfo.last_name} on ${new Date(statusInfo.approved_at!).toLocaleDateString()}`
                : statusInfo?.status === 'in_review'
                ? 'Waiting for review and approval'
                : statusInfo?.status === 'published'
                ? 'Published and visible to students'
                : 'Draft - in progress'}
            </span>
          </div>
          
          {(isAdmin() || statusInfo?.status === 'draft') && (
            <div className="flex flex-col space-y-2">
              <label className="text-sm font-medium">Update Status:</label>
              <Select
                disabled={isUpdating}
                defaultValue={statusInfo?.status}
                onValueChange={handleStatusChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="in_review">Submit for Review</SelectItem>
                  {isAdmin() && (
                    <>
                      <SelectItem value="approved">Approve</SelectItem>
                      <SelectItem value="published">Publish</SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default ModuleStatus;
